import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import { next } from '@ember/runloop';

import PhotosApi from 'mewe/api/photos-api';
import FunctionalUtils from 'mewe/shared/functional-utils';
import { Theme } from 'mewe/constants';
import { isValidUrlParam } from 'mewe/utils/miscellaneous-utils';
import { restartableTask, timeout } from 'ember-concurrency';
import { installDropdown } from 'mewe/utils/dropdown-utils';

export default class MwAlbumPicker extends Component {
  limit = 15;

  @tracked albums = A();
  @tracked addToAlbumDropdownOpened;
  @tracked albumName = '';
  @tracked isNameInvalid;

  constructor() {
    super(...arguments);

    installDropdown(this, 'addToAlbumDropdownOpened');

    next(() => {
      if (this.args.isAddToAlbumDialog) {
        this.setDropdownRef?.(true);
        this.focusAlbumInput();
      }

      // adding photos to preselected album -> check if it's full (MW-387)
      // adding photo post to album -> load albums upon opening dialog
      if (this.args.selectedAlbumName || this.args.isAddToAlbumDialog) {
        this.loadAlbums.perform(true);
      }
    });
  }

  @action
  onInsert(element) {
    this.element = element;
  }

  get albumNameValue() {
    return (this.albumName || '').trim();
  }

  @restartableTask
  *loadAlbums(loadFromConstructor = false) {
    yield timeout(400);

    // chekcing for true because it can be also event object
    loadFromConstructor = loadFromConstructor === true;
    // reset error state on any change
    this.isNameInvalid = false;

    const eventId = this.args.eventId,
      groupId = this.args.groupId,
      pageId = this.args.pageId;

    let params = {
        limit: this.limit,
        toAdd: this.args.photosLength,
        term: loadFromConstructor ? this.args.selectedAlbumName : this.albumNameValue
      },
      scope,
      scopeId;

    if (eventId) {
      scopeId = eventId;
      scope = Theme.EVENT;
    } else if (pageId) {
      scopeId = pageId;
      scope = Theme.PAGE;
    } else if (groupId && groupId !== Theme.GROUPS && groupId !== Theme.MYCLOUD) {
      if (groupId !== 'contacts') {
        scopeId = groupId;
        scope = Theme.GROUP;
      }
    } else if (this.args.target === Theme.MYCLOUD) {
      scope = Theme.MYCLOUD;
    }

    const callback = (data) => {
      if (this.isDestroyed || this.isDestroying) return;

      // adding photos to preselected album -> check if it's full (MW-387)
      if (loadFromConstructor && this.args.selectedAlbumName) {
        if (data.albums?.[0]?.full) {
          this.args.setAlbumName('');
          FunctionalUtils.error(__('Album size limit reached.'));
        }
      } else {
        this.albums = data.albums || A();
      }
    };

    const fail = (resp) => {
      if (resp && resp.status) {
        FunctionalUtils.showDefaultErrorMessage();
      }
    };

    PhotosApi.getAlbumNames(scope, scopeId, params).then(callback).catch(fail);
  }

  focusAlbumInput() {
    scheduleOnce('afterRender', this, () => {
      if (this.isDestroying || this.isDestroyed) return;

      const inputEl = this.element.querySelector('.album-picker_content input');
      inputEl?.focus();
    });
  }

  validateAlbumName() {
    const value = this.albumNameValue;
    const isExisitAndFull = this.albums.find(album => album.name === value && album.full);
    let isNotValid = !value || value.trim().length === 0 || isExisitAndFull;

    if (!isNotValid) {
      if (!isValidUrlParam(value)) {
        isNotValid = true;
        FunctionalUtils.error(__('Album name is incorrect'));
      }
    }

    this.isNameInvalid = isNotValid;
  }

  @action
  addAlbum() {
    this.validateAlbumName();

    if (!this.isNameInvalid) {
      this.setDropdownRef?.(false);
      this.args.setAlbumName(this.albumNameValue);
    }
  }

  @action
  selectAlbum(album) {
    if (!album) {
      this.args.setAlbumName('');
      return;
    }

    if (album.full) {
      return;
    }

    this.setDropdownRef?.(false);
    this.args.setAlbumName(album.name);
  }

  @action
  toggleDropdown() {
    if (this.addToAlbumDropdownOpened) {
      this.setDropdownRef?.(false);
    } else {
      this.openAddToAlbumDropdown();
    }
  }

  @action
  openAddToAlbumDropdown() {
    // clean error if there is any from previous opening
    this.isNameInvalid = false;
    this.albumName = '';

    this.setDropdownRef?.(true);
    this.loadAlbums.perform();
    this.focusAlbumInput();
  }
}
